'use client';

/**
 * @brisa/ui · components/ui/button — shadcn Button (Facelift v1)
 *
 * Feature: migracao-facelift-v1 · ADR §D1 (shadcn adoption) + §D6 (placement)
 *
 * Variantes cva canônicas per COMPONENT_MAP §1 + test F1-BTN-01..06:
 *   - primary-large   (162×64, pill amarelo, Bodoni Moda? — typography from parent)
 *   - primary-medium  (139×48, pill amarelo)
 *   - primary-small   (113×40, pill amarelo)
 *   - secondary-pill  (pill text-32 bg + amarelo text — "Em breve")
 *   - terciary-ghost  (texto + opcional icon, sem fundo)
 *   - terciary-link   (texto-only, underline-on-hover — legal contexts)
 *   - icon-circle     (40×40 botão circular — 3 hierarquias via variant prop)
 *
 * 'use client' obrigatório (ADR Armadilha #3 + F1-RSC-01) — Radix Slot
 * (via asChild) requer Client Component boundary.
 */
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';

export const buttonVariants = cva(
  // base — focus-visible ring amarelo + transitions + a11y
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium ' +
    'transition-all duration-base ease-out ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ' +
    'disabled:pointer-events-none disabled:opacity-50 ' +
    'aria-disabled:pointer-events-none aria-disabled:opacity-50',
  {
    variants: {
      variant: {
        // PRIMARY family — amarelo bg + texto preto, pill
        'primary-large': 'rounded-full bg-accent-yellow text-foreground hover:bg-[hsl(var(--accent-yellow-dim))] hover:scale-[0.98] active:scale-[0.96]',
        'primary-medium': 'rounded-full bg-accent-yellow text-foreground hover:bg-[hsl(var(--accent-yellow-dim))] hover:scale-[0.98] active:scale-[0.96]',
        'primary-small': 'rounded-full bg-accent-yellow text-foreground hover:bg-[hsl(var(--accent-yellow-dim))] hover:scale-[0.98] active:scale-[0.96]',
        // SECONDARY — pill escuro semitransparente, label amarela
        'secondary-pill':
          'rounded-full bg-[hsl(var(--text-32))] text-accent-yellow hover:bg-[hsl(var(--text-40))]',
        // TERCIARY — texto-only
        'terciary-ghost':
          'rounded-full bg-transparent text-[hsl(var(--text-64))] hover:bg-[hsl(var(--text-08))]',
        'terciary-link':
          'bg-transparent text-[hsl(var(--text-64))] underline-offset-4 hover:underline rounded-none',
        // ICON — quadrado-arredondado 40×40 com 3 hierarquias visuais
        'icon-circle':
          'rounded-full bg-accent-yellow text-foreground hover:bg-[hsl(var(--accent-yellow-dim))] hover:scale-[0.98] active:scale-[0.96]',
        // shadcn defaults (mantidos para retrocompat com outros consumers)
        default: 'rounded-full bg-accent-yellow text-foreground hover:bg-[hsl(var(--accent-yellow-dim))] hover:scale-[0.98] active:scale-[0.96]',
        destructive: 'rounded-full bg-destructive text-destructive-foreground hover:brightness-95',
        outline:
          'rounded-full border border-border bg-transparent text-foreground hover:bg-[hsl(var(--text-08))]',
        secondary: 'rounded-full bg-secondary text-secondary-foreground hover:brightness-95',
        ghost: 'rounded-full bg-transparent hover:bg-[hsl(var(--text-08))]',
        link: 'bg-transparent text-[hsl(var(--text-64))] underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-10 px-4 text-sm', // 113×40 — primary-small
        md: 'h-12 px-6 text-base', // 139×48 — primary-medium
        lg: 'h-16 px-8 text-lg', // 162×64 — primary-large
        icon: 'h-10 w-10 p-0', // 40×40 — icon-circle
        default: 'h-12 px-6 text-base',
      },
    },
    compoundVariants: [
      // Size implícito por variant (caller pode override via size prop)
      { variant: 'primary-large', size: 'default', class: 'h-16 px-8 text-lg' },
      { variant: 'primary-medium', size: 'default', class: 'h-12 px-6 text-base' },
      { variant: 'primary-small', size: 'default', class: 'h-10 px-4 text-sm' },
      { variant: 'icon-circle', size: 'default', class: 'h-10 w-10 p-0' },
    ],
    defaultVariants: {
      variant: 'primary-large',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref as React.Ref<HTMLButtonElement>}
        type={!asChild ? (type ?? 'button') : undefined}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';
